/**
 * DrawingToolsManager – manages interactive drawing tools on a lightweight-charts canvas overlay.
 *
 * Supported tools:
 *   - horizontalLine   : a horizontal price line that spans the full width
 *   - trendLine        : a line between two (time,price) anchor points
 *   - fibonacciRetracement : Fibonacci levels between two price points
 *   - channel          : two parallel trend lines (base + offset)
 *
 * Persistence is via localStorage keyed by symbol + timeframe.
 */

// ─── helpers ──────────────────────────────────────────────────────────────────
const STORAGE_PREFIX = 'chartDrawings:v1:';
const STORAGE_PREFIX_V2 = 'chartDrawings:v2:';

function storageKey(symbol) {
  return `${STORAGE_PREFIX_V2}${symbol || 'default'}`;
}

function uid() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const FIBONACCI_LEVELS = [0, 0.236, 0.382, 0.5, 0.618, 0.786, 1];
const FIBONACCI_EXTENSIONS = [1.272, 1.618];
const ALL_FIBONACCI_LEVELS = [...FIBONACCI_LEVELS, ...FIBONACCI_EXTENSIONS];

const TOOL_TYPES = {
  NONE: 'none',
  HORIZONTAL_LINE: 'horizontalLine',
  TREND_LINE: 'trendLine',
  FIBONACCI: 'fibonacciRetracement',
  CHANNEL: 'channel',
};

const DEFAULT_STYLE = {
  color: '#FFD700',
  lineWidth: 1.5,
  fontSize: 11,
  labelBg: 'rgba(30,41,59,0.85)',
};

// ─── Manager class ────────────────────────────────────────────────────────────
class DrawingToolsManager {
  constructor() {
    /** @type {HTMLCanvasElement|null} */
    this.canvas = null;
    this.ctx = null;

    // Lightweight-charts refs (set via attach)
    this.chart = null;
    this.series = null;

    // Drawing state
    this.drawings = []; // persisted array of drawing objects
    this.activeTool = TOOL_TYPES.NONE;
    this._pendingPoints = []; // points collected while drawing
    this._hoverPoint = null;  // current mouse position in (time,price)
    this._hoverPixel = null;  // current mouse position in (x,y)
    this._selectedId = null;  // id of the selected drawing
    this._hoveredId = null;   // id of drawing under cursor
    this._dragging = null;    // { drawingId, anchorIndex, startX, startY }
    this._isDirty = true;

    // Context key for persistence
    this._symbol = null;
    this._timeframe = null;

    // Bound event handlers
    this._onMouseDown = this._onMouseDown.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onMouseUp = this._onMouseUp.bind(this);
    this._onDblClick = this._onDblClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);
    this._onParentMouseMove = this._onParentMouseMove.bind(this);
    this._rafId = null;
    this._renderLoop = this._renderLoop.bind(this);

    // External callback: notify React when drawing list changes
    this.onChange = null;
  }

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  /**
   * Attach the manager to a chart & canvas.
   * @param {HTMLCanvasElement} canvas  overlay canvas element
   * @param {object} chart              lightweight-charts IChartApi
   * @param {object} series             main candlestick series
   * @param {string} symbol
   * @param {string} timeframe
   */
  attach(canvas, chart, series, symbol, timeframe) {
    this.detach(); // clean up previous if any

    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.chart = chart;
    this.series = series;
    this._symbol = symbol;
    this._timeframe = timeframe;

    // Size the canvas to its parent
    this._resize();

    // Load persisted drawings
    this._load();

    // Events on the canvas (active when pointer-events: auto)
    canvas.addEventListener('mousedown', this._onMouseDown);
    canvas.addEventListener('mousemove', this._onMouseMove);
    canvas.addEventListener('mouseup', this._onMouseUp);
    canvas.addEventListener('dblclick', this._onDblClick);
    window.addEventListener('keydown', this._onKeyDown);

    // Listen on the parent container to detect hover over drawings even
    // when the overlay canvas has pointer-events: none
    this._parentEl = canvas.parentElement;
    if (this._parentEl) {
      this._parentEl.addEventListener('mousemove', this._onParentMouseMove);
    }

    // Subscribe to chart time-scale changes to re-render
    try {
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => { this._isDirty = true; });
    } catch { /* ignore */ }

    // Observe canvas resize
    this._resizeObserver = new ResizeObserver(() => this._resize());
    this._resizeObserver.observe(canvas.parentElement || canvas);

    // Start render loop
    this._isDirty = true;
    this._rafId = requestAnimationFrame(this._renderLoop);
  }

  detach() {
    if (this._rafId) cancelAnimationFrame(this._rafId);
    this._rafId = null;
    if (this.canvas) {
      this.canvas.removeEventListener('mousedown', this._onMouseDown);
      this.canvas.removeEventListener('mousemove', this._onMouseMove);
      this.canvas.removeEventListener('mouseup', this._onMouseUp);
      this.canvas.removeEventListener('dblclick', this._onDblClick);
    }
    if (this._parentEl) {
      this._parentEl.removeEventListener('mousemove', this._onParentMouseMove);
      this._parentEl = null;
    }
    window.removeEventListener('keydown', this._onKeyDown);
    try { this._resizeObserver?.disconnect(); } catch { /* ignore */ }
    this._resizeObserver = null;
    this.canvas = null;
    this.ctx = null;
    this.chart = null;
    this.series = null;
  }

  // ── Public API ──────────────────────────────────────────────────────────────

  setTool(toolType) {
    this.activeTool = toolType || TOOL_TYPES.NONE;
    this._pendingPoints = [];
    this._isDirty = true;
    // Set cursor style
    if (this.canvas) {
      this.canvas.style.cursor = toolType && toolType !== TOOL_TYPES.NONE ? 'crosshair' : 'default';
      // Enable pointer events only when a tool is active or we're hovering/selected
      this.canvas.style.pointerEvents = toolType && toolType !== TOOL_TYPES.NONE ? 'auto' : 'none';
    }
  }

  /** Enable pointer events for selection even when no tool is active */
  enableSelection() {
    if (this.canvas) this.canvas.style.pointerEvents = 'auto';
  }

  disableSelection() {
    if (this.canvas && this.activeTool === TOOL_TYPES.NONE) {
      this.canvas.style.pointerEvents = 'none';
    }
  }

  deleteSelected() {
    if (!this._selectedId) return;
    this.drawings = this.drawings.filter(d => d.id !== this._selectedId);
    this._selectedId = null;
    this._save();
    this._isDirty = true;
    this.onChange?.();
  }

  deleteAll() {
    this.drawings = [];
    this._selectedId = null;
    this._save();
    this._isDirty = true;
    this.onChange?.();
  }

  getDrawings() {
    return this.drawings;
  }

  getSelectedId() {
    return this._selectedId;
  }

  getSelectedDrawing() {
    if (!this._selectedId) return null;
    return this.drawings.find(d => d.id === this._selectedId) || null;
  }

  /** Update a drawing's visual properties (color, lineWidth, fibLevels, stretched) by id */
  updateDrawing(id, updates) {
    const drawing = this.drawings.find(d => d.id === id);
    if (!drawing) return;
    if (updates.color !== undefined) drawing.color = updates.color;
    if (updates.lineWidth !== undefined) drawing.lineWidth = updates.lineWidth;
    if (updates.fibLevels !== undefined) drawing.fibLevels = updates.fibLevels;
    if (updates.stretched !== undefined) drawing.stretched = updates.stretched;
    this._save();
    this._isDirty = true;
    this.onChange?.();
  }

  /** Select a drawing by id (for programmatic selection from the list) */
  selectDrawing(id) {
    this._selectedId = id || null;
    this._isDirty = true;
    this.onChange?.();
  }

  /** Change the context (symbol/timeframe) and reload drawings on symbol change */
  setContext(symbol, timeframe) {
    const symbolChanged = this._symbol !== symbol;
    this._symbol = symbol;
    this._timeframe = timeframe;
    if (symbolChanged) {
      this._load();
    }
    this._isDirty = true;
  }

  /** Update series ref (e.g. after chart re-init) */
  updateSeries(series) {
    this.series = series;
    this._isDirty = true;
  }

  /** Update chart ref (e.g. after chart re-init) */
  updateChart(chart) {
    this.chart = chart;
    try {
      this.chart.timeScale().subscribeVisibleLogicalRangeChange(() => { this._isDirty = true; });
    } catch { /* ignore */ }
    this._isDirty = true;
  }

  markDirty() {
    this._isDirty = true;
  }

  // ── Coordinate Conversion ───────────────────────────────────────────────────

  _timeToX(time) {
    try { return this.chart?.timeScale()?.timeToCoordinate(time); } catch { return null; }
  }

  _logicalToX(logical) {
    try { return this.chart?.timeScale()?.logicalToCoordinate(logical); } catch { return null; }
  }

  /**
   * Convert a point object { time?, logical?, price } to an x-pixel.
   * Tries time-based conversion first, then falls back to logical index.
   */
  _pointToX(pt) {
    if (!pt) return null;
    if (pt.time != null) {
      const x = this._timeToX(pt.time);
      if (x != null) return x;
    }
    if (pt.logical != null) {
      return this._logicalToX(pt.logical);
    }
    return null;
  }

  _xToTime(x) {
    try { return this.chart?.timeScale()?.coordinateToTime(x); } catch { return null; }
  }

  _xToLogical(x) {
    try { return this.chart?.timeScale()?.coordinateToLogical(x); } catch { return null; }
  }

  _priceToY(price) {
    try { return this.series?.priceToCoordinate(price); } catch { return null; }
  }

  _yToPrice(y) {
    try { return this.series?.coordinateToPrice(y); } catch { return null; }
  }

  /**
   * Convert pixel (x,y) to a point object with time, logical, and price.
   * Works even beyond the last candle by using logical coordinates.
   */
  _pixelToPoint(x, y) {
    const price = this._yToPrice(y);
    if (price == null) return null;
    const time = this._xToTime(x);
    const logical = this._xToLogical(x);
    // Need at least one x-coordinate system to work
    if (time == null && logical == null) return null;
    return { time, logical, price };
  }

  // ── Resize ──────────────────────────────────────────────────────────────────

  _resize() {
    if (!this.canvas) return;
    const parent = this.canvas.parentElement;
    if (!parent) return;
    const dpr = window.devicePixelRatio || 1;
    const w = parent.clientWidth;
    const h = parent.clientHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    this.ctx?.scale(dpr, dpr);
    this._isDirty = true;
  }

  // ── Persistence ─────────────────────────────────────────────────────────────

  _save() {
    try {
      const key = storageKey(this._symbol);
      localStorage.setItem(key, JSON.stringify(this.drawings));
    } catch { /* ignore */ }
  }

  _load() {
    try {
      const key = storageKey(this._symbol);
      let raw = localStorage.getItem(key);

      // Migration: merge any old per-timeframe keys (v1) into the new symbol-only key (v2)
      if (!raw) {
        const merged = [];
        const seenIds = new Set();
        const prefix = `${STORAGE_PREFIX}${this._symbol || 'default'}:`;
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(prefix)) {
            try {
              const items = JSON.parse(localStorage.getItem(k));
              if (Array.isArray(items)) {
                for (const d of items) {
                  if (d && d.id && !seenIds.has(d.id)) {
                    seenIds.add(d.id);
                    merged.push(d);
                  }
                }
              }
              // Remove old key after migration
              localStorage.removeItem(k);
              i--; // adjust index since we removed an item
            } catch { /* skip bad entries */ }
          }
        }
        if (merged.length > 0) {
          this.drawings = merged;
          this._save();
          return;
        }
      }

      this.drawings = raw ? JSON.parse(raw) : [];
    } catch {
      this.drawings = [];
    }
  }

  // ── Hit Testing ─────────────────────────────────────────────────────────────

  _hitTest(px, py, threshold = 8) {
    // Walk drawings in reverse (topmost first)
    for (let i = this.drawings.length - 1; i >= 0; i--) {
      const d = this.drawings[i];
      const hit = this._hitTestDrawing(d, px, py, threshold);
      if (hit) return { drawingId: d.id, anchorIndex: hit.anchorIndex };
    }
    return null;
  }

  _hitTestDrawing(drawing, px, py, threshold) {
    switch (drawing.type) {
      case TOOL_TYPES.HORIZONTAL_LINE:
        return this._hitHorizontalLine(drawing, px, py, threshold);
      case TOOL_TYPES.TREND_LINE:
        return this._hitTrendLine(drawing, px, py, threshold);
      case TOOL_TYPES.FIBONACCI:
        return this._hitFibonacci(drawing, px, py, threshold);
      case TOOL_TYPES.CHANNEL:
        return this._hitChannel(drawing, px, py, threshold);
      default:
        return null;
    }
  }

  _hitHorizontalLine(d, px, py, thr) {
    const y = this._priceToY(d.price);
    if (y == null) return null;
    if (Math.abs(py - y) <= thr) return { anchorIndex: -1 };
    return null;
  }

  _hitTrendLine(d, px, py, thr) {
    if (!d.points || d.points.length < 2) return null;
    // Check anchors first
    for (let i = 0; i < d.points.length; i++) {
      const ax = this._pointToX(d.points[i]);
      const ay = this._priceToY(d.points[i].price);
      if (ax != null && ay != null && Math.hypot(px - ax, py - ay) <= thr + 4) {
        return { anchorIndex: i };
      }
    }
    // Check line proximity
    const x1 = this._pointToX(d.points[0]);
    const y1 = this._priceToY(d.points[0].price);
    const x2 = this._pointToX(d.points[1]);
    const y2 = this._priceToY(d.points[1].price);
    if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
    if (this._distToSegment(px, py, x1, y1, x2, y2) <= thr) return { anchorIndex: -1 };
    return null;
  }

  _hitFibonacci(d, px, py, thr) {
    if (!d.points || d.points.length < 2) return null;
    // Check anchors
    for (let i = 0; i < d.points.length; i++) {
      const ax = this._pointToX(d.points[i]);
      const ay = this._priceToY(d.points[i].price);
      if (ax != null && ay != null && Math.hypot(px - ax, py - ay) <= thr + 4) {
        return { anchorIndex: i };
      }
    }
    // Check fib levels (respect bounded / stretched)
    const p0 = d.points[0].price;
    const p1 = d.points[1].price;
    const diff = p1 - p0;
    const enabledLevels = d.fibLevels || ALL_FIBONACCI_LEVELS;
    const isStretched = d.stretched !== false;
    const x1 = this._pointToX(d.points[0]);
    const x2 = this._pointToX(d.points[1]);
    const xLeft = x1 != null && x2 != null ? Math.min(x1, x2) : null;
    const xRight = x1 != null && x2 != null ? Math.max(x1, x2) : null;
    for (const level of enabledLevels) {
      const price = p0 + diff * (1 - level);
      const y = this._priceToY(price);
      if (y == null) continue;
      if (Math.abs(py - y) > thr) continue;
      // In bounded mode, only hit-test within the x range
      if (!isStretched && xLeft != null && xRight != null) {
        if (px < xLeft - thr || px > xRight + thr) continue;
      }
      return { anchorIndex: -1 };
    }
    return null;
  }

  _hitChannel(d, px, py, thr) {
    if (!d.points || d.points.length < 2) return null;
    // Check anchors (3 points: 2 for base line + 1 for offset)
    for (let i = 0; i < d.points.length; i++) {
      const ax = this._pointToX(d.points[i]);
      const ay = this._priceToY(d.points[i].price);
      if (ax != null && ay != null && Math.hypot(px - ax, py - ay) <= thr + 4) {
        return { anchorIndex: i };
      }
    }
    // Check base line + parallel
    const x1 = this._pointToX(d.points[0]);
    const y1 = this._priceToY(d.points[0].price);
    const x2 = this._pointToX(d.points[1]);
    const y2 = this._priceToY(d.points[1].price);
    if (x1 == null || y1 == null || x2 == null || y2 == null) return null;
    if (this._distToSegment(px, py, x1, y1, x2, y2) <= thr) return { anchorIndex: -1 };
    if (d.points.length >= 3) {
      const offset = d.points[2].price - d.points[0].price;
      const ya = this._priceToY(d.points[0].price + offset);
      const yb = this._priceToY(d.points[1].price + offset);
      if (ya != null && yb != null && this._distToSegment(px, py, x1, ya, x2, yb) <= thr) return { anchorIndex: -1 };
    }
    return null;
  }

  _distToSegment(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) return Math.hypot(px - x1, py - y1);
    let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
  }

  // ── Mouse Event Handlers ────────────────────────────────────────────────────

  _getCanvasXY(e) {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  _onMouseDown(e) {
    if (!this.canvas) return;
    const { x, y } = this._getCanvasXY(e);

    // If a tool is active, add points
    if (this.activeTool !== TOOL_TYPES.NONE) {
      e.preventDefault();
      e.stopPropagation();
      const pt = this._pixelToPoint(x, y);
      if (!pt) return;

      switch (this.activeTool) {
        case TOOL_TYPES.HORIZONTAL_LINE:
          this._commitDrawing({ id: uid(), type: TOOL_TYPES.HORIZONTAL_LINE, price: pt.price, color: '#FFD700' });
          break;

        case TOOL_TYPES.TREND_LINE:
          this._pendingPoints.push(pt);
          if (this._pendingPoints.length === 2) {
            this._commitDrawing({ id: uid(), type: TOOL_TYPES.TREND_LINE, points: [...this._pendingPoints], color: '#00BFFF' });
            this._pendingPoints = [];
          }
          break;

        case TOOL_TYPES.FIBONACCI:
          this._pendingPoints.push(pt);
          if (this._pendingPoints.length === 2) {
            this._commitDrawing({
              id: uid(),
              type: TOOL_TYPES.FIBONACCI,
              points: [...this._pendingPoints],
              color: '#FFD700',
              fibLevels: [...ALL_FIBONACCI_LEVELS],
              stretched: false,
            });
            this._pendingPoints = [];
          }
          break;

        case TOOL_TYPES.CHANNEL:
          this._pendingPoints.push(pt);
          if (this._pendingPoints.length === 3) {
            this._commitDrawing({ id: uid(), type: TOOL_TYPES.CHANNEL, points: [...this._pendingPoints], color: '#9370DB' });
            this._pendingPoints = [];
          }
          break;
      }
      this._isDirty = true;
      return;
    }

    // Selection / dragging mode
    const hit = this._hitTest(x, y);
    if (hit) {
      e.preventDefault();
      e.stopPropagation();
      this._selectedId = hit.drawingId;
      this._dragging = { drawingId: hit.drawingId, anchorIndex: hit.anchorIndex, startX: x, startY: y, origPoints: null };
      // Store original points for dragging
      const drawing = this.drawings.find(d => d.id === hit.drawingId);
      if (drawing) {
        this._dragging.origPoints = drawing.points ? drawing.points.map(p => ({ ...p })) : null;
        this._dragging.origPrice = drawing.price;
      }
      this._isDirty = true;
      this.onChange?.();
    } else {
      if (this._selectedId) {
        this._selectedId = null;
        this._isDirty = true;
        this.onChange?.();
      }
    }
  }

  _onMouseMove(e) {
    if (!this.canvas) return;
    const { x, y } = this._getCanvasXY(e);
    this._hoverPixel = { x, y };
    const pt = this._pixelToPoint(x, y);
    this._hoverPoint = pt;

    // Dragging
    if (this._dragging) {
      e.preventDefault();
      e.stopPropagation();
      const drawing = this.drawings.find(d => d.id === this._dragging.drawingId);
      if (!drawing) return;
      const dx = x - this._dragging.startX;
      const dy = y - this._dragging.startY;

      if (drawing.type === TOOL_TYPES.HORIZONTAL_LINE) {
        const newY = this._priceToY(this._dragging.origPrice) + dy;
        const newPrice = this._yToPrice(newY);
        if (newPrice != null) drawing.price = newPrice;
      } else if (drawing.points && this._dragging.origPoints) {
        if (this._dragging.anchorIndex >= 0) {
          // Move single anchor
          const orig = this._dragging.origPoints[this._dragging.anchorIndex];
          const origX = this._pointToX(orig);
          const origY = this._priceToY(orig.price);
          if (origX != null && origY != null) {
            const newX = origX + dx;
            const newTime = this._xToTime(newX);
            const newLogical = this._xToLogical(newX);
            const newPrice = this._yToPrice(origY + dy);
            if ((newTime != null || newLogical != null) && newPrice != null) {
              drawing.points[this._dragging.anchorIndex] = { time: newTime, logical: newLogical, price: newPrice };
            }
          }
        } else {
          // Move whole drawing
          for (let i = 0; i < drawing.points.length; i++) {
            const orig = this._dragging.origPoints[i];
            const origX = this._pointToX(orig);
            const origY = this._priceToY(orig.price);
            if (origX != null && origY != null) {
              const newX = origX + dx;
              const newTime = this._xToTime(newX);
              const newLogical = this._xToLogical(newX);
              const newPrice = this._yToPrice(origY + dy);
              if ((newTime != null || newLogical != null) && newPrice != null) {
                drawing.points[i] = { time: newTime, logical: newLogical, price: newPrice };
              }
            }
          }
        }
      }
      this._isDirty = true;
      return;
    }

    // Update cursor based on hover
    if (this.activeTool === TOOL_TYPES.NONE) {
      const hit = this._hitTest(x, y);
      const newHoveredId = hit ? hit.drawingId : null;
      if (newHoveredId !== this._hoveredId) {
        this._hoveredId = newHoveredId;
        this._isDirty = true;
      }
      // Change canvas pointer-events so chart can receive events when not hovering
      if (newHoveredId || this._selectedId) {
        this.canvas.style.cursor = hit?.anchorIndex >= 0 ? 'grab' : 'pointer';
        this.canvas.style.pointerEvents = 'auto';
      } else {
        this.canvas.style.cursor = 'default';
        // Allow chart interaction when not on a drawing
        this.canvas.style.pointerEvents = 'none';
      }
    }

    // Preview while drawing
    if (this._pendingPoints.length > 0) {
      this._isDirty = true;
    }
  }

  _onMouseUp(e) {
    if (this._dragging) {
      e.preventDefault();
      e.stopPropagation();
      this._dragging = null;
      this._save();
      this._isDirty = true;
    }
  }

  /**
   * Listens on the parent container (always receives events) to detect
   * when the cursor approaches a drawing, then enables pointer-events
   * on the overlay canvas so it can capture mousedown/drag.
   */
  _onParentMouseMove(e) {
    // Skip when a tool is active (canvas already has pointer-events: auto)
    if (this.activeTool !== TOOL_TYPES.NONE) return;
    // Skip when currently dragging (canvas already capturing)
    if (this._dragging) return;
    if (!this.canvas || this.drawings.length === 0) return;

    const rect = this.canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const hit = this._hitTest(x, y, 10);

    if (hit) {
      // Cursor is near a drawing — enable canvas so it can receive click/drag
      this.canvas.style.pointerEvents = 'auto';
      this.canvas.style.cursor = hit.anchorIndex >= 0 ? 'grab' : 'pointer';
      if (hit.drawingId !== this._hoveredId) {
        this._hoveredId = hit.drawingId;
        this._isDirty = true;
      }
    } else {
      // Not near any drawing — let chart handle events
      if (this._hoveredId) {
        this._hoveredId = null;
        this._isDirty = true;
      }
      if (!this._selectedId) {
        this.canvas.style.pointerEvents = 'none';
        this.canvas.style.cursor = 'default';
      }
    }
  }

  _onDblClick(e) {
    // Double-click on drawing to select & allow deletion
    const { x, y } = this._getCanvasXY(e);
    const hit = this._hitTest(x, y);
    if (hit) {
      e.preventDefault();
      e.stopPropagation();
      this._selectedId = hit.drawingId;
      this._isDirty = true;
      this.onChange?.();
    }
  }

  _onKeyDown(e) {
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (this._selectedId) {
        // Only delete if the focus isn't on an input field
        const tag = document.activeElement?.tagName?.toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
        e.preventDefault();
        this.deleteSelected();
      }
    }
    if (e.key === 'Escape') {
      if (this.activeTool !== TOOL_TYPES.NONE) {
        this._pendingPoints = [];
        this.setTool(TOOL_TYPES.NONE);
        this.onChange?.();
      } else if (this._selectedId) {
        this._selectedId = null;
        this._isDirty = true;
        this.onChange?.();
      }
    }
  }

  // ── Drawing management ──────────────────────────────────────────────────────

  _commitDrawing(drawing) {
    this.drawings.push(drawing);
    this._selectedId = drawing.id;
    this._save();
    this.onChange?.();
  }

  // ── Render Loop ─────────────────────────────────────────────────────────────

  _renderLoop() {
    if (this._isDirty) {
      this._isDirty = false;
      this._render();
    }
    this._rafId = requestAnimationFrame(this._renderLoop);
  }

  _render() {
    const ctx = this.ctx;
    if (!ctx || !this.canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const w = this.canvas.width / dpr;
    const h = this.canvas.height / dpr;

    ctx.save();
    ctx.clearRect(0, 0, w, h);

    // Draw all persisted drawings
    for (const drawing of this.drawings) {
      const isSelected = drawing.id === this._selectedId;
      const isHovered = drawing.id === this._hoveredId;
      this._renderDrawing(ctx, drawing, w, h, isSelected, isHovered);
    }

    // Draw preview (in-progress drawing)
    if (this._pendingPoints.length > 0 && this._hoverPoint) {
      this._renderPreview(ctx, w, h);
    }

    ctx.restore();
  }

  _renderDrawing(ctx, drawing, w, h, isSelected, isHovered) {
    switch (drawing.type) {
      case TOOL_TYPES.HORIZONTAL_LINE:
        this._drawHorizontalLine(ctx, drawing, w, h, isSelected, isHovered);
        break;
      case TOOL_TYPES.TREND_LINE:
        this._drawTrendLine(ctx, drawing, w, h, isSelected, isHovered);
        break;
      case TOOL_TYPES.FIBONACCI:
        this._drawFibonacci(ctx, drawing, w, h, isSelected, isHovered);
        break;
      case TOOL_TYPES.CHANNEL:
        this._drawChannel(ctx, drawing, w, h, isSelected, isHovered);
        break;
    }
  }

  // ── Rendering individual drawing types ──────────────────────────────────────

  _drawHorizontalLine(ctx, d, w, _h, isSelected, isHovered) {
    const y = this._priceToY(d.price);
    if (y == null) return;
    const color = d.color || DEFAULT_STYLE.color;
    const baseLw = d.lineWidth || DEFAULT_STYLE.lineWidth;
    const lw = isSelected ? baseLw + 1 : isHovered ? baseLw + 0.5 : baseLw;

    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    if (!isSelected) ctx.setLineDash([6, 4]);
    else ctx.setLineDash([]);
    ctx.moveTo(0, y);
    ctx.lineTo(w, y);
    ctx.stroke();
    ctx.setLineDash([]);

    // Price label on right
    const label = d.price.toFixed(5);
    ctx.font = `${DEFAULT_STYLE.fontSize}px monospace`;
    const tw = ctx.measureText(label).width;
    const lx = w - tw - 12;
    const ly = y - 6;
    ctx.fillStyle = DEFAULT_STYLE.labelBg;
    ctx.fillRect(lx - 4, ly - DEFAULT_STYLE.fontSize + 2, tw + 8, DEFAULT_STYLE.fontSize + 4);
    ctx.fillStyle = color;
    ctx.fillText(label, lx, ly);

    // Selection anchors
    if (isSelected) {
      this._drawAnchor(ctx, 30, y, color);
      this._drawAnchor(ctx, w - 30, y, color);
    }
  }

  _drawTrendLine(ctx, d, w, _h, isSelected, isHovered) {
    if (!d.points || d.points.length < 2) return;
    const x1 = this._pointToX(d.points[0]);
    const y1 = this._priceToY(d.points[0].price);
    const x2 = this._pointToX(d.points[1]);
    const y2 = this._priceToY(d.points[1].price);
    if (x1 == null || y1 == null || x2 == null || y2 == null) return;

    const color = d.color || '#00BFFF';
    const baseLw = d.lineWidth || DEFAULT_STYLE.lineWidth;
    const lw = isSelected ? baseLw + 1 : isHovered ? baseLw + 0.5 : baseLw;

    // Extend line beyond the segment to the edges of the chart
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy);
    if (len > 0) {
      const extendFactor = Math.max(w, 2000) / len;
      const ex1 = x1 - dx * extendFactor;
      const ey1 = y1 - dy * extendFactor;
      const ex2 = x2 + dx * extendFactor;
      const ey2 = y2 + dy * extendFactor;

      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.3;
      ctx.lineWidth = 1;
      ctx.setLineDash([4, 4]);
      ctx.moveTo(ex1, ey1);
      ctx.lineTo(ex2, ey2);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    // Main segment
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    if (isSelected) {
      this._drawAnchor(ctx, x1, y1, color);
      this._drawAnchor(ctx, x2, y2, color);
    }
  }

  _drawFibonacci(ctx, d, w, _h, isSelected, isHovered) {
    if (!d.points || d.points.length < 2) return;
    const x1 = this._pointToX(d.points[0]);
    const x2 = this._pointToX(d.points[1]);
    if (x1 == null || x2 == null) return;

    const p0 = d.points[0].price;
    const p1 = d.points[1].price;
    const diff = p1 - p0;
    const color = d.color || DEFAULT_STYLE.color;
    const enabledLevels = d.fibLevels || ALL_FIBONACCI_LEVELS;
    const isStretched = d.stretched !== false;

    // Bounded x-range (with a small margin for labels)
    const xLeft = Math.min(x1, x2);
    const xRight = Math.max(x1, x2);
    const drawLeft = isStretched ? 0 : xLeft;
    const drawRight = isStretched ? w : xRight;
    const labelX = isStretched ? 4 : xLeft + 4;

    const levelColors = {
      0: '#787B86',
      0.236: '#F44336',
      0.382: '#FF9800',
      0.5: '#4CAF50',
      0.618: '#00BCD4',
      0.786: '#2196F3',
      1: '#787B86',
      1.272: '#9C27B0',
      1.618: '#E91E63',
    };

    for (const level of enabledLevels) {
      const price = p0 + diff * (1 - level);
      const y = this._priceToY(price);
      if (y == null) continue;

      const lColor = levelColors[level] || color;
      const lw = (level === 0 || level === 1) ? 1 : (isHovered ? 1.5 : 1);

      ctx.beginPath();
      ctx.strokeStyle = lColor;
      ctx.lineWidth = lw;
      ctx.globalAlpha = level > 1 ? 0.5 : 0.8;
      ctx.setLineDash(level > 1 ? [4, 4] : []);
      ctx.moveTo(drawLeft, y);
      ctx.lineTo(drawRight, y);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;

      // Label
      const label = `${(level * 100).toFixed(1)}%  ${price.toFixed(5)}`;
      ctx.font = `${DEFAULT_STYLE.fontSize - 1}px monospace`;
      const tw = ctx.measureText(label).width;
      ctx.fillStyle = DEFAULT_STYLE.labelBg;
      ctx.fillRect(labelX, y - DEFAULT_STYLE.fontSize + 1, tw + 8, DEFAULT_STYLE.fontSize + 2);
      ctx.fillStyle = lColor;
      ctx.fillText(label, labelX + 4, y - 2);
    }

    // Shading between 38.2% and 61.8% (golden pocket) – only if both levels are enabled
    const has382 = enabledLevels.includes(0.382);
    const has618 = enabledLevels.includes(0.618);
    if (has382 && has618) {
      const y382 = this._priceToY(p0 + diff * (1 - 0.382));
      const y618 = this._priceToY(p0 + diff * (1 - 0.618));
      if (y382 != null && y618 != null) {
        ctx.fillStyle = 'rgba(255, 215, 0, 0.04)';
        ctx.fillRect(drawLeft, Math.min(y382, y618), drawRight - drawLeft, Math.abs(y618 - y382));
      }
    }

    // Vertical boundary lines in bounded mode for visual clarity
    if (!isStretched) {
      const yTop = this._priceToY(p0 + diff * (1 - Math.max(...enabledLevels)));
      const yBot = this._priceToY(p0 + diff * (1 - Math.min(...enabledLevels)));
      if (yTop != null && yBot != null) {
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.25;
        ctx.lineWidth = 1;
        ctx.setLineDash([3, 3]);
        ctx.moveTo(xLeft, Math.min(yTop, yBot));
        ctx.lineTo(xLeft, Math.max(yTop, yBot));
        ctx.moveTo(xRight, Math.min(yTop, yBot));
        ctx.lineTo(xRight, Math.max(yTop, yBot));
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.globalAlpha = 1;
      }
    }

    // Anchors
    if (isSelected) {
      const y0 = this._priceToY(p0);
      const y1p = this._priceToY(p1);
      if (y0 != null) this._drawAnchor(ctx, x1, y0, color);
      if (y1p != null) this._drawAnchor(ctx, x2, y1p, color);
    }
  }

  _drawChannel(ctx, d, w, _h, isSelected, isHovered) {
    if (!d.points || d.points.length < 2) return;
    const x1 = this._pointToX(d.points[0]);
    const y1 = this._priceToY(d.points[0].price);
    const x2 = this._pointToX(d.points[1]);
    const y2 = this._priceToY(d.points[1].price);
    if (x1 == null || y1 == null || x2 == null || y2 == null) return;

    const color = d.color || '#9370DB';
    const baseLw = d.lineWidth || DEFAULT_STYLE.lineWidth;
    const lw = isSelected ? baseLw + 1 : isHovered ? baseLw + 0.5 : baseLw;

    // Base line
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.lineWidth = lw;
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();

    // Parallel line (offset defined by 3rd point)
    let offset = 0;
    if (d.points.length >= 3) {
      offset = d.points[2].price - d.points[0].price;
    }
    const y1b = this._priceToY(d.points[0].price + offset);
    const y2b = this._priceToY(d.points[1].price + offset);
    if (y1b != null && y2b != null) {
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = lw;
      ctx.setLineDash([6, 3]);
      ctx.moveTo(x1, y1b);
      ctx.lineTo(x2, y2b);
      ctx.stroke();
      ctx.setLineDash([]);

      // Fill between
      ctx.fillStyle = color.replace(/[\d.]+\)$/, '0.06)').replace(/^#/, '');
      // Convert hex to rgba for fill
      const fillColor = this._hexToRgba(color, 0.06);
      ctx.fillStyle = fillColor;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.lineTo(x2, y2b);
      ctx.lineTo(x1, y1b);
      ctx.closePath();
      ctx.fill();

      // Mid-line
      const y1m = (y1 + y1b) / 2;
      const y2m = (y2 + y2b) / 2;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.3;
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 5]);
      ctx.moveTo(x1, y1m);
      ctx.lineTo(x2, y2m);
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }

    if (isSelected) {
      this._drawAnchor(ctx, x1, y1, color);
      this._drawAnchor(ctx, x2, y2, color);
      if (d.points.length >= 3) {
        const ax = this._pointToX(d.points[2]);
        const ay = this._priceToY(d.points[2].price);
        if (ax != null && ay != null) this._drawAnchor(ctx, ax, ay, color);
      }
    }
  }

  // ── Preview (in-progress line) ──────────────────────────────────────────────

  _renderPreview(ctx, w, h) {
    const pts = this._pendingPoints;
    const hp = this._hoverPoint;
    if (!hp) return;

    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.setLineDash([4, 4]);

    switch (this.activeTool) {
      case TOOL_TYPES.TREND_LINE:
        if (pts.length === 1) {
          const x1 = this._pointToX(pts[0]);
          const y1 = this._priceToY(pts[0].price);
          const x2 = this._hoverPixel?.x;
          const y2 = this._hoverPixel?.y;
          if (x1 != null && y1 != null && x2 != null && y2 != null) {
            ctx.beginPath();
            ctx.strokeStyle = '#00BFFF';
            ctx.lineWidth = 1.5;
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
        }
        break;

      case TOOL_TYPES.FIBONACCI:
        if (pts.length === 1) {
          const p0 = pts[0].price;
          const p1 = hp.price;
          const diff = p1 - p0;
          const fx1 = this._pointToX(pts[0]);
          const fx2 = this._hoverPixel?.x;
          const fLeft = fx1 != null && fx2 != null ? Math.min(fx1, fx2) : 0;
          const fRight = fx1 != null && fx2 != null ? Math.max(fx1, fx2) : w;
          for (const level of FIBONACCI_LEVELS) {
            const price = p0 + diff * (1 - level);
            const y = this._priceToY(price);
            if (y == null) continue;
            ctx.beginPath();
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 1;
            ctx.moveTo(fLeft, y);
            ctx.lineTo(fRight, y);
            ctx.stroke();
          }
        }
        break;

      case TOOL_TYPES.CHANNEL:
        if (pts.length === 1) {
          const x1 = this._pointToX(pts[0]);
          const y1 = this._priceToY(pts[0].price);
          const x2 = this._hoverPixel?.x;
          const y2 = this._hoverPixel?.y;
          if (x1 != null && y1 != null && x2 != null && y2 != null) {
            ctx.beginPath();
            ctx.strokeStyle = '#9370DB';
            ctx.lineWidth = 1.5;
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
          }
        } else if (pts.length === 2) {
          // Show base line solid, parallel preview
          const x1 = this._pointToX(pts[0]);
          const y1 = this._priceToY(pts[0].price);
          const x2 = this._pointToX(pts[1]);
          const y2 = this._priceToY(pts[1].price);
          if (x1 != null && y1 != null && x2 != null && y2 != null) {
            // Base
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.strokeStyle = '#9370DB';
            ctx.lineWidth = 1.5;
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
            // Parallel preview
            const offset = hp.price - pts[0].price;
            const y1b = this._priceToY(pts[0].price + offset);
            const y2b = this._priceToY(pts[1].price + offset);
            if (y1b != null && y2b != null) {
              ctx.setLineDash([4, 4]);
              ctx.beginPath();
              ctx.moveTo(x1, y1b);
              ctx.lineTo(x2, y2b);
              ctx.stroke();
            }
          }
        }
        break;
    }

    ctx.restore();
  }

  // ── Utilities ───────────────────────────────────────────────────────────────

  _drawAnchor(ctx, x, y, color) {
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
    ctx.strokeStyle = '#fff';
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }

  _hexToRgba(hex, alpha) {
    if (!hex || typeof hex !== 'string') return `rgba(0,0,0,${alpha})`;
    const h = hex.replace('#', '');
    if (h.length < 6) return `rgba(0,0,0,${alpha})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  }
}

export { DrawingToolsManager, TOOL_TYPES, ALL_FIBONACCI_LEVELS };
export default DrawingToolsManager;
